import { lowerFirst } from "lodash-es"

import { DetectionPill } from "@/components/terminal/detection-pill"
import { DetectionPopover } from "@/components/terminal/detection-popover"
import { dataByDetectionClass } from "@/constants/terminal/detection-class"
import { detectionSentenceLabel } from "@/lib/terminal/detection-type-label"
import type { TerminalDetectionView } from "@/lib/terminal/file-view"
import { cn } from "@/lib/utils"
import { AGING_NOTE, type DetectionChange } from "@/terminal/digest/file-change"

// What happened in one reassessment (Dhanur, 24 Oct 2026): a list of lines,
// each led by the sign of the way it pushed the score, + up and − down, in the
// muted grey of a date (owner ruling, 2026-10-26). A line is a
// detection, shown as its class in the detection pill (the same tinted pill
// a card uses for its type, here carrying the class) and its type underlined,
// which pops the detection open; or a short plain sentence, which is not a
// link. No headline, no verb, no bullet marks.
export type WhatHappenedLine =
  | {
      kind: "detection"
      direction: "up" | "down"
      detection: TerminalDetectionView
      // A new Detection reads as new; an aging one leads with its event's year
      // and says it weighs less with age.
      change?: DetectionChange | null
    }
  | { kind: "sentence"; direction: "up" | "down"; text: string }

const Sign = ({ direction }: { direction: "up" | "down" }) => (
  <span
    aria-hidden="true"
    className="type-data text-muted-foreground w-3 shrink-0 text-center leading-5"
  >
    {direction === "up" ? "+" : "−"}
  </span>
)

// The underlined type that opens the detection: a new one reads as new, an
// aging one leads with its event's year.
const triggerLabel = (
  detection: TerminalDetectionView,
  change?: DetectionChange | null
) => {
  const label = detectionSentenceLabel(detection)
  if (change === "new") return `New ${lowerFirst(label)}`
  if (change === "aging")
    return [detection.eventDate?.slice(0, 4), label].filter(Boolean).join(" ")
  return label
}

export const WhatHappened = ({
  lines,
  className,
}: {
  lines: WhatHappenedLine[]
  className?: string
}) => (
  <ul className={cn("flex flex-col gap-(--terminal-space-line)", className)}>
    {lines.map((line, index) => (
      <li key={index} className="type-row-label flex items-start gap-2">
        <Sign direction={line.direction} />
        <span className="sr-only">
          {line.direction === "up" ? "raised the score:" : "lowered the score:"}
        </span>
        {line.kind === "sentence" ? (
          <span>{line.text}</span>
        ) : (
          // Inline text, not a flex row, so a narrow column wraps the line
          // like a sentence instead of breaking each part onto its own line.
          <span className="[&>*]:align-middle">
            <DetectionPill
              label={dataByDetectionClass[line.detection.class].label}
              detectionClass={line.detection.class}
            />{" "}
            <DetectionPopover detection={line.detection}>
              {triggerLabel(line.detection, line.change)}
            </DetectionPopover>
            {line.change === "aging" && (
              <span className="text-muted-foreground"> {AGING_NOTE}</span>
            )}
          </span>
        )}
      </li>
    ))}
  </ul>
)
